// A product typeahead over module 01's active and draft products (spec 03 §4.3, §4.12).
//
// Rev-7 (design-system §3.17 FilterBar, UP4; D-560 ruling 1): the suggestions
// open only while the box has focus and float over the page, as the catalogue
// picker's do. The list used to render whenever two letters stood in the box,
// so a term left behind after the reader moved on kept a row of product
// buttons painted across the form below it. It opens on focus or on typing,
// closes when focus leaves the picker, and Escape closes it and marks the key
// handled, so a FilterBar panel around the picker stays open (one press, one
// layer). ArrowDown moves from the box into the list and the arrows walk it;
// ArrowUp from the first option returns to the box.

import { useId, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Field } from "../../../../components/field";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { useProductSearch, type Product } from "../hooks/planning-queries";

export function ProductPicker(props: {
  readonly label: string;
  readonly onPick: (product: Product) => void;
  readonly exclude?: ReadonlySet<string>;
  readonly disabled?: boolean;
  readonly "data-testid"?: string;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const listId = useId();
  const root = useRef<HTMLDivElement | null>(null);
  const input = useRef<HTMLInputElement | null>(null);
  const q = useDebouncedValue(text.trim(), 250);
  const search = useProductSearch(q.length >= 2 ? q : "");
  const testId = props["data-testid"] ?? "planning-product-picker";
  const items = (search.data?.items ?? []).filter((p) => !(props.exclude?.has(p.id) ?? false));
  // The box's own text decides too, not only the debounced term: a pick
  // empties the box, and the 250 ms term would otherwise reopen the list with
  // the old results the moment focus returns to the box.
  const showing = open && text.trim().length >= 2 && q.length >= 2;

  const options = (): HTMLButtonElement[] =>
    Array.from(root.current?.querySelectorAll<HTMLButtonElement>(".planning-picker-option") ?? []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      if (!showing) return;
      // Handled here: a FilterBar panel around the picker leaves this Escape
      // alone and closes on the next one.
      event.preventDefault();
      // Focus first: moving it from an option into the box fires the root's
      // onFocus, which opens the list, and the close must be the last word.
      input.current?.focus();
      setOpen(false);
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const list = options();
    if (list.length === 0) return;
    const at = list.findIndex((el) => el === document.activeElement);
    if (at === -1) {
      if (event.key !== "ArrowDown") return;
      event.preventDefault();
      setOpen(true);
      list[0]?.focus();
      return;
    }
    event.preventDefault();
    if (event.key === "ArrowUp" && at === 0) {
      input.current?.focus();
      return;
    }
    const next = event.key === "ArrowDown" ? Math.min(at + 1, list.length - 1) : at - 1;
    list[next]?.focus();
  };

  return (
    <div
      ref={root}
      className="planning-picker"
      data-testid={testId}
      data-open={showing ? "true" : "false"}
      onFocus={() => {
        setOpen(true);
      }}
      onBlur={(event) => {
        const next = event.relatedTarget;
        if (next instanceof Node && root.current?.contains(next) === true) return;
        setOpen(false);
      }}
      onKeyDown={onKeyDown}
    >
      <Field label={props.label} wide>
        <input
          ref={input}
          type="search"
          role="combobox"
          aria-expanded={showing}
          aria-controls={showing ? listId : undefined}
          aria-autocomplete="list"
          autoComplete="off"
          value={text}
          disabled={props.disabled === true}
          placeholder={t("planning.picker.placeholder")}
          data-testid={`${testId}-input`}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
          }}
        />
      </Field>
      {showing ? (
        <ul
          id={listId}
          className="planning-picker-list"
          role="listbox"
          aria-label={t("planning.picker.results")}
          data-testid={`${testId}-results`}
        >
          {search.isPending ? (
            <li className="planning-hint" role="status">
              {t("planning.picker.searching")}
            </li>
          ) : search.error !== null ? (
            <li className="planning-hint" role="alert">
              {t("planning.picker.error")}
            </li>
          ) : items.length === 0 ? (
            <li className="planning-hint">{t("planning.picker.none")}</li>
          ) : (
            items.map((p) => (
              <li key={p.id} role="option" aria-selected={false}>
                <button
                  type="button"
                  className="planning-picker-option"
                  data-testid={`${testId}-option`}
                  // Keep focus in the picker while the press lands, so the
                  // list does not close under the pointer before the click.
                  onMouseDown={(event) => {
                    event.preventDefault();
                  }}
                  onClick={() => {
                    props.onPick(p);
                    setText("");
                    // The option is about to unmount; without this a pick by
                    // Enter drops keyboard focus to the page. Focus first,
                    // because the box's focus reopens the list and the close
                    // must be the last word (the Escape path's order).
                    input.current?.focus();
                    setOpen(false);
                  }}
                >
                  <span className="planning-sku">{p.sku}</span> {p.name}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
